import React, { useEffect, useState } from "react";
import axios from "axios";
import config from "../../config";
import { getThemeColors, getComponentColors, Theme } from "../../theme/colors";
import ConfigDetails, {
  ConfigDetailsData,
  ConfigDetailsStyles,
} from "./components/ConfigDetails";

interface CurrentRdmProps {
  username: string;
  theme: Theme;
  devNumber: number;
}

export const CurrentRdm: React.FC<CurrentRdmProps> = ({
  username,
  theme,
  devNumber,
}) => {
  const colors = getThemeColors(username, theme);
  const componentColors = getComponentColors("table", theme);

  const [detailsLoading, setDetailsLoading] = useState<boolean>(true);
  const [detailsError, setDetailsError] = useState<string | null>(null);
  const [configDetails, setConfigDetails] = useState<ConfigDetailsData | null>(
    null
  );
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean>(false);

  const detailsStyles: ConfigDetailsStyles = {
    container: {
      marginTop: "20px",
      padding: "16px",
      backgroundColor: componentColors.background || colors.background,
      color: colors.text,
      border: `1px solid ${colors.border}`,
      borderRadius: "8px",
    },
    tabBar: {
      display: "flex",
      gap: "8px",
      marginBottom: "12px",
    },
    tabButton: {
      backgroundColor: "transparent",
      color: colors.text,
      border: `1px solid ${colors.border}`,
      padding: "6px 12px",
      borderRadius: "4px",
      cursor: "pointer",
    },
    tabButtonActive: {
      backgroundColor: colors.optionsBg,
      color: colors.text,
      border: `1px solid ${colors.border}`,
      padding: "6px 12px",
      borderRadius: "4px",
      cursor: "pointer",
    },
    section: {
      display: "flex",
      flexDirection: "column",
      gap: "8px",
    },
    row: {
      display: "grid",
      gridTemplateColumns: "200px 1fr",
      gap: "12px",
      padding: "8px 0",
      borderBottom: `1px solid ${colors.border}`,
    },
    label: {
      fontWeight: 600,
    },
    value: {},
  };

  useEffect(() => {
    const fetchDetails = async () => {
      setDetailsLoading(true);
      setDetailsError(null);
      setConfigDetails(null);
      try {
        const response = await axios.post(
          `${config.api.baseUrl}/chat/current_rdm_config`,
          {
            username,
          }
        );
        setConfigDetails(response.data?.config ?? null);
        setHasUnsavedChanges(false);
      } catch (err: unknown) {
        const message =
          typeof err === "object" && err !== null && "response" in err
            ? (err as { response?: { data?: { error?: string } } }).response
                ?.data?.error || "Failed to load config details"
            : "Failed to load config details";
        setDetailsError(message);
      } finally {
        setDetailsLoading(false);
      }
    };

    fetchDetails();
  }, [username]);

  return (
    <div
      style={{
        padding: "20px",
        height: "calc(100vh - 48px)",
        backgroundColor: componentColors.background || colors.background,
        color: colors.text,
        overflowY: "auto",
      }}
    >
      <h1
        style={{
          marginBottom: "20px",
          color: colors.text,
          fontSize: "24px",
          fontWeight: "bold",
        }}
      >
        Current RDM Configuration
        {configDetails && (
          <span
            style={{
              color: hasUnsavedChanges ? "#ff6b35" : colors.text,
              opacity: hasUnsavedChanges ? 1 : 0.8,
              fontWeight: hasUnsavedChanges ? "bold" : "normal",
              marginLeft: "8px",
            }}
          >
            {hasUnsavedChanges
              ? "(Unsaved changes)"
              : `(ID: ${configDetails.id})`}
          </span>
        )}
      </h1>

      {detailsLoading && (
        <div style={{ textAlign: "center", padding: "20px" }}>
          <div>Loading configuration...</div>
        </div>
      )}

      {detailsError && (
        <div
          style={{
            padding: "16px",
            backgroundColor: "#f8d7da",
            color: "#721c24",
            border: "1px solid #f5c6cb",
            borderRadius: "4px",
            marginTop: "20px",
          }}
        >
          Error: {detailsError}
        </div>
      )}

      {!detailsLoading && !detailsError && configDetails && (
        <ConfigDetails
          configData={configDetails}
          username={username}
          theme={theme}
          styles={detailsStyles}
          onConfigChange={setHasUnsavedChanges}
          devNumber={devNumber}
          labels={{
            promptbox: "Conversation Agent",
            sql_promptbox: "SQL Agent",
            specific_definitions: "Definitions: Domain",
            general_definitions: "SQL Instructions",
            other_definitions: "Definitions: Client",
            nr_defs_used: "Max number of definitions to use",
            config_name: "Config Name",
            created_at: "Created At",
            db_schema: "DB Schema",
            db_tables: "DB Tables",
            sample_rows_in_table_info: "Number of samples to use in table info",
            max_nr_rows_from_sql_query:
              "Max number of rows provided for the prompt from the SQL results",
            db_database: "Database",
            id: "ID",
          }}
        />
      )}
    </div>
  );
};
